import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Play, RefreshCw, TrendingUp, Link as LinkIcon, Package, BarChart3, Globe, Megaphone, Coins, AlertTriangle, PlusCircle, Trash2, MapPin, X, ChevronDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import ScheduleConfig from '../components/ScheduleConfig';
import HistoryCalendar from '../components/HistoryCalendar';
import PdfDownloadButton from '../components/PdfDownloadButton';
import WebmasterChat from '../components/WebmasterChat';
import PromoTab from '../components/PromoTab';
import LocationSelector from '../components/LocationSelector';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const AI_ENGINE_COLORS = {
  'ChatGPT': '#10B981',
  'Perplexity': '#22B8CF',
  'Google AI': '#F59E0B',

  'Grok': '#EF4444',
  'Google AI Overviews': '#EC4899',
  'Google AI Mode': '#84CC16',
  'Bing Copilot': '#F97316',
  'You.com': '#7C3AED'
};

const MonitorDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [monitor, setMonitor] = useState(null);
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [timeFilter, setTimeFilter] = useState(30);
  const [chartMetric, setChartMetric] = useState('visibility');
  const [creatingSubmonitor, setCreatingSubmonitor] = useState(null);
  const [activeTab, setActiveTab] = useState('monitoring');
  const [creditBalance, setCreditBalance] = useState(null);
  const [opCosts, setOpCosts] = useState({});
  const [newProduct, setNewProduct] = useState({ name: '', url: '', description: '' });
  const [addingProduct, setAddingProduct] = useState(false);
  const [newQuery, setNewQuery] = useState('');
  const [customQueries, setCustomQueries] = useState([]);
  const [savingQueries, setSavingQueries] = useState(false);
  const [geoLocations, setGeoLocations] = useState([]);
  const startingGeoIds = useRef(new Set());
  const [showGeoAdd, setShowGeoAdd] = useState(false);
  const [geoForm, setGeoForm] = useState({ country: '', region: null, city: null });
  const [addingGeo, setAddingGeo] = useState(false);
  const [selectedGeoView, setSelectedGeoView] = useState('all'); // 'all' or geo_id
  const [geoResultsCache, setGeoResultsCache] = useState({}); // { geo_id: results }
    // D4: Company info editing
  const [editingCompanyInfo, setEditingCompanyInfo] = useState(false);
  const [savingCompanyInfo, setSavingCompanyInfo] = useState(false);
  const [pixelSnippet, setPixelSnippet] = useState('');
  const [pixelStats, setPixelStats] = useState(null);
  const [snippetCopied, setSnippetCopied] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    company_name: '', activities: '', products_services: '',
    target_audience: '', usp: '', geography: '',
    brand_aliases: '', exclusion_contexts: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Reset states when monitor changes
    setCompanyForm({
      company_name: '', activities: '', products_services: '',
      target_audience: '', usp: '', geography: '',
      brand_aliases: '', exclusion_contexts: ''
    });
    setEditingCompanyInfo(false);
    setSavingCompanyInfo(false);
    setPixelSnippet('');
    setPixelStats(null);
    setSnippetCopied(false);
  }, [id]);

  useEffect(() => {
    const loadPixelData = async () => {
      try {
        const [snippetRes, statsRes] = await Promise.all([
          axios.get(`${API_URL}/api/pixel/${id}/snippet`, { withCredentials: true }).catch(() => null),
          axios.get(`${API_URL}/api/pixel/${id}/stats`, { withCredentials: true }).catch(() => null),
        ]);
        if (snippetRes?.data?.snippet) setPixelSnippet(snippetRes.data.snippet);
        if (statsRes?.data) setPixelStats(statsRes.data);
      } catch (e) { /* silent */ }
    };
    loadPixelData();
  }, [id]);

  useEffect(() => {
    loadData();
    loadBillingInfo();
    loadGeoLocations();
    const interval = setInterval(() => {
      if (monitor?.status === 'analyzing') {
        loadData();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [id, monitor?.status, timeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBillingInfo = async () => {
    try {
      const [balRes, costRes] = await Promise.all([
        axios.get(`${API_URL}/api/billing/balance`, { withCredentials: true }),
        axios.get(`${API_URL}/api/billing/operation-costs`, { withCredentials: true }),
      ]);
      setCreditBalance(balRes.data.balance);
      setOpCosts(costRes.data);
    } catch (e) { /* silent */ }
  };

  // D4: Company info form handlers
  const openCompanyForm = (info) => {
    setCompanyForm({
      company_name: info?.company_name || '',
      activities: (info?.activities || []).join(', '),
      products_services: (info?.products_services || []).join(', '),
      target_audience: info?.target_audience || '',
      usp: info?.usp || '',
      geography: info?.geography || '',
      brand_aliases: (info?.brand_aliases || []).join(', '),
      exclusion_contexts: (info?.exclusion_contexts || []).join(', '),
    });
    setEditingCompanyInfo(true);
  };

  const handleSaveCompanyInfo = async () => {
    if (!companyForm.company_name.trim()) {
      toast.error(t('monitor.название_компании_обязательно'));
      return;
    }
    setSavingCompanyInfo(true);
    try {
      const payload = {
        company_name: companyForm.company_name.trim(),
        activities: companyForm.activities ? companyForm.activities.split(',').map(s => s.trim()).filter(Boolean) : [],
        products_services: companyForm.products_services ? companyForm.products_services.split(',').map(s => s.trim()).filter(Boolean) : [],
        target_audience: (companyForm.target_audience || '').trim(),
        usp: (companyForm.usp || '').trim(),
        brand_tone: '',
        geography: (companyForm.geography || '').trim(),
        brand_aliases: companyForm.brand_aliases ? companyForm.brand_aliases.split(',').map(s => s.trim()).filter(Boolean) : [],
        exclusion_contexts: companyForm.exclusion_contexts ? companyForm.exclusion_contexts.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      await axios.put(`${API_URL}/api/monitors/${id}/company-info`, payload, { withCredentials: true });
      toast.success(t('monitor.профиль_сохранён'));
      setEditingCompanyInfo(false);
      loadData();
    } catch (err) {
      console.error('[D4] Save error:', err); toast.error(err.response?.data?.detail || err.message || t('monitor.ошибка_сохранения'));
    } finally {
      setSavingCompanyInfo(false);
    }
  };

    const loadGeoLocations = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/monitors/${id}/geo`);
      setGeoLocations(data || []);
      // Load results for summary table
      const cache = {};
      for (const geo of (data || [])) {
        if (geo.status === 'completed') {
          try {
            const res = await axios.get(`${API_URL}/api/geo/${geo.id}/results`);
            if (res.data && Object.keys(res.data).length > 0) {
              cache[geo.id] = res.data;
            }
          } catch (e) { /* skip */ }
        }
      }
      setGeoResultsCache(cache);
    } catch (e) { /* silent */ }
  };

  const handleAddGeo = async () => {
    if (!geoForm.country) return;
    setAddingGeo(true);
    try {
      await axios.post(`${API_URL}/api/monitors/${id}/geo`, {
        country: geoForm.country || '',
        region: geoForm.region || null,
        city: geoForm.city || null,
        language: geoForm.language || 'en',  // SESSION 59: pass language from LocationSelector
      });
      toast.success(t('monitor.локация_добавлена'));
      setGeoForm({ country: '', region: null, city: null });
      setShowGeoAdd(false);
      loadGeoLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('monitor.ошибка_добавления_локации'));
    } finally {
      setAddingGeo(false);
    }
  };

  const handleDeleteGeo = async (geoId) => {
    try {
      await axios.delete(`${API_URL}/api/monitors/${id}/geo/${geoId}`, { withCredentials: true });
      toast.success(t('monitor.локация_удалена'));
      loadGeoLocations();
    } catch (err) {
      toast.error(t('monitor.ошибка_удаления_локации'));
    }
  };

  const copyPixelSnippet = () => {
    navigator.clipboard.writeText(pixelSnippet).then(() => {
      setSnippetCopied(true);
      setTimeout(() => setSnippetCopied(false), 2000);
      toast.success(t('monitor.код_скопирован'));
    }).catch(() => toast.error(t('monitor.не_удалось_скопировать')));
  };

  const handleStartGeoAnalysis = async (geoId) => {
    // Double-click protection
    if (startingGeoIds.current.has(geoId)) return;
    console.log('[ARPAI-BTN] click', geoId, monitor?.company_info?.company_name);
    startingGeoIds.current.add(geoId);
    // Instantly update UI — show spinner
    setGeoLocations(prev => prev.map(g => g.id === geoId ? { ...g, status: 'analyzing' } : g));
    try {
      await axios.post(`${API_URL}/api/geo/${geoId}/analyze`, {}, { withCredentials: true });
      loadGeoLocations();
      loadBillingInfo();
    } catch (err) {
      // Show spinner for at least 1 sec
      await new Promise(r => setTimeout(r, 1000));
      console.log('[ARPAI-BTN] error', err.response?.status, err.message);
      loadGeoLocations();
      if (err.response?.status === 402) {
        toast.error(err.response.data.detail || t('monitor.недостаточно_кредитов'));
      } else if (err.response?.status === 409) {
        toast.warn(t('monitor.мониторинг_уже_запущен'));
      } else if (err.response?.status === 422) {
        const detail = err.response.data?.detail || '';
        if (detail.includes('robots')) {
          toast.warn(t('monitor.сайт_блокирует'));
        } else {
          toast.warn(t('monitor.не_удалось_определить_профиль'));
        }
        openCompanyForm(monitor?.company_info || {});
      } else if (!err.response || err.code === 'ECONNABORTED') {
        toast.error(t('monitor.сервер_недоступен'));
      } else {
        const detail = err.response?.data?.detail;
        toast.error(detail || t('monitor.не_удалось_запустить'));
      }
    } finally {
      startingGeoIds.current.delete(geoId);
    }
  };

  const loadData = async () => {
    try {
      const [monitorRes, resultsRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/monitors/${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/monitors/${id}/results`, { withCredentials: true }),
        axios.get(`${API_URL}/api/monitors/${id}/history?days=${timeFilter}`, { withCredentials: true })
      ]);

      setMonitor(monitorRes.data);
      setResults(resultsRes.data);
      setHistory(historyRes.data);
      setCustomQueries(monitorRes.data.custom_queries || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartAnalysis = async () => {
    setAnalyzing(true);
    // Immediately update local state to show "analyzing" status
    setMonitor(prev => prev ? { ...prev, status: 'analyzing' } : prev);
    toast.info(t('monitor.анализ_запущен'));
    try {
      await axios.post(
        `${API_URL}/api/monitors/${id}/analyze`,
        {},
        { withCredentials: true }
      );
      loadBillingInfo();
      setTimeout(() => {
        loadData();
      }, 2000);
    } catch (error) {
      // Revert status on error
      loadData();
      if (error.response?.status === 402) {
        toast.error(error.response.data.detail || t('monitor.недостаточно_кредитов'));
      } else {
        toast.error(t('monitor.ошибка_запуска_анализа'));
        console.error('Error starting analysis:', error);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleProductClick = async (productName) => {
    setCreatingSubmonitor(productName);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/monitors/${id}/submonitors`,
        { product_name: productName },
        { withCredentials: true }
      );
      navigate(`/monitor/${id}/product/${data.id}`);
    } catch (error) {
      console.error('Error creating submonitor:', error);
    } finally {
      setCreatingSubmonitor(null);
    }
  };

  const handleAddProduct = async () => {
    if (!newProduct.name.trim()) return;
    setAddingProduct(true);
    try {
      await axios.post(
        `${API_URL}/api/monitors/${id}/products`,
        newProduct,
        { withCredentials: true }
      );
      setNewProduct({ name: '', url: '', description: '' });
      loadData();
    } catch (error) {
      console.error('Error adding product:', error);
    } finally {
      setAddingProduct(false);
    }
  };

  const handleRemoveProduct = async (productId) => {
    try {
      await axios.delete(`${API_URL}/api/monitors/${id}/products/${productId}`, { withCredentials: true });
      loadData();
    } catch (error) {
      console.error('Error removing product:', error);
    }
  };

  const handleAddQuery = async () => {
    const trimmed = newQuery.trim();
    if (!trimmed || customQueries.includes(trimmed)) return;
    const updatedQueries = [...customQueries, trimmed];
    setCustomQueries(updatedQueries);
    setNewQuery('');
    // Auto-save immediately
    try {
      await axios.put(
        `${API_URL}/api/monitors/${id}/custom-queries`,
        { queries: updatedQueries },
        { withCredentials: true }
      );
      toast.success(t('monitor.запрос_добавлен'));
    } catch (error) {
      toast.error(t('monitor.ошибка_сохранения_запроса'));
      console.error('Error saving query:', error);
    }
  };

  const handleRemoveQuery = async (idx) => {
    const updatedQueries = customQueries.filter((_, i) => i !== idx);
    setCustomQueries(updatedQueries);
    // Auto-save immediately
    try {
      await axios.put(
        `${API_URL}/api/monitors/${id}/custom-queries`,
        { queries: updatedQueries },
        { withCredentials: true }
      );
      toast.success(t('monitor.запрос_удалён'));
    } catch (error) {
      toast.error(t('monitor.ошибка_сохранения'));
      console.error('Error saving queries:', error);
    }
  };

  const handleSaveQueries = async () => {
    setSavingQueries(true);
    try {
      await axios.put(
        `${API_URL}/api/monitors/${id}/custom-queries`,
        { queries: customQueries },
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Error saving queries:', error);
    } finally {
      setSavingQueries(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFFFF]">
        <div className="text-2xl font-bold text-[#0F172A]">{t('common.загрузка')}</div>
      </div>
    );
  }

  if (!monitor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFFFF]">
        <div className="text-2xl font-bold text-red-600">{t('monitor.не_найден')}</div>
      </div>
    );
  }

  const chartData = history.map(h => {
    const dataPoint = {
      date: new Date(h.created_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
      sov: h.sov_overall,
      market_sov: h.market_sov,
      brand_sov: h.brand_sov,
    };
    
    Object.entries(h.ai_engine_results || {}).forEach(([engine, data]) => {
      if (chartMetric === 'visibility') {
        dataPoint[engine] = data.visibility_percent;
      } else {
        dataPoint[`${engine}_citation`] = data.citation_percent;
      }
    });
    
    return dataPoint;
  });

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <header className="bg-white/80 backdrop-blur-xl border-b border-[#E2DDD8] sticky top-0 z-50">
        <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-[#1BB594] hover:underline font-semibold"
              data-testid="back-to-dashboard-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-baseline gap-1.5">
              <img src="/arpai_logo.png" alt="ARPAI" style={{height: '1.25rem'}} />
              <span className="text-xl font-bold text-[#2F2E2F] tracking-[0.15em]">GEO</span>
              <span className="hidden sm:inline text-[10px] text-slate-400 ml-2">{t('monitor.сервис_слоган')}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Project Tabs */}
      <div className="bg-white border-b border-[#E2DDD8]">
        <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12">
          <nav className="flex gap-0" data-testid="project-tabs">
            <button
              onClick={() => setActiveTab('monitoring')}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-bold uppercase tracking-widest border-b-2 transition-colors ${
                activeTab === 'monitoring'
                  ? 'border-[#1BB594] text-[#1BB594]'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
              data-testid="tab-monitoring"
            >
              <BarChart3 className="w-4 h-4" />
              {t('monitor.вкладка_мониторинг')}
            </button>
            <button
              onClick={() => setActiveTab('webmaster')}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-bold uppercase tracking-widest border-b-2 transition-colors ${
                activeTab === 'webmaster'
                  ? 'border-[#1BB594] text-[#1BB594]'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
              data-testid="tab-webmaster"
            >
              <Globe className="w-4 h-4" />
              {t('monitor.вкладка_вебмастер')}
            </button>
            <button
              onClick={() => setActiveTab('promotion')}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-bold uppercase tracking-widest border-b-2 transition-colors ${
                activeTab === 'promotion'
                  ? 'border-[#1BB594] text-[#1BB594]'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
              data-testid="tab-promotion"
            >
              <Megaphone className="w-4 h-4" />
              {t('monitor.вкладка_продвижение')}
            </button>
          </nav>
        </div>
      </div>

      <main id="pdf-content" className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="mb-12 flex justify-between items-start">
          <div>
            <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-black text-[#2F2E2F] mb-4" data-testid="monitor-title">
              {monitor.company_info?.company_name || monitor.company_url}
            </h1>
            <div className="flex items-center gap-4">
              {monitor.country && (
                <>
                  <span className="text-base text-slate-600">{monitor.country}</span>
                  <span className="text-slate-400">•</span>
                </>
              )}
              <span className="text-base text-slate-600">{monitor.company_url}</span>
            </div>
          </div>
        </div>

        {activeTab === 'monitoring' && (
          <div data-testid="monitoring-tab-content">

            {/* Geo-location tabs */}
            <div className="mb-8" data-testid="geo-locations-block">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-bold uppercase tracking-widest text-slate-500">{t('monitor.локации_мониторинга')}</span>
              </div>
              <div className="flex items-center flex-wrap gap-2 mb-3">
                {/* "All locations" tab */}
                <button
                  onClick={() => setSelectedGeoView('all')}
                  className={`flex items-center gap-2 px-4 py-2 border transition-colors ${selectedGeoView === 'all' ? 'bg-[#1BB594] text-white border-[#1BB594]' : 'bg-white border-[#E2DDD8] text-slate-700 hover:border-[#1BB594]/50'}`}
                  data-testid="geo-tab-all"
                >
                  <BarChart3 className="w-3.5 h-3.5" />
                  <span className="text-sm font-bold">{t('monitor.все_локации')}</span>
                </button>

                {/* Add location */}
                {!showGeoAdd ? (
                  <button
                    onClick={() => setShowGeoAdd(true)}
                    className="flex items-center gap-1.5 px-4 py-2 border border-dashed border-[#E2DDD8] text-slate-400 hover:border-[#1BB594] hover:text-[#1BB594] transition-colors"
                    data-testid="geo-add-btn"
                  >
                    <PlusCircle className="w-3.5 h-3.5" />
                    <span className="text-xs font-bold uppercase tracking-widest">{t('monitor.добавить_локацию')}</span>
                  </button>
                ) : (
                  <div className="w-full mt-2 p-4 bg-slate-50 border border-[#E2DDD8]" data-testid="geo-add-form">
                    <LocationSelector value={geoForm} onChange={setGeoForm} />
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={handleAddGeo}
                        disabled={addingGeo || !geoForm.country}
                        className="text-xs font-bold uppercase tracking-widest px-6 py-2 bg-[#1BB594] text-white hover:bg-[#159A7F] disabled:opacity-50"
                        data-testid="geo-save-btn"
                      >
                        {addingGeo ? '...' : t('monitor.добавить')}
                      </button>
                      <button
                        onClick={() => { setShowGeoAdd(false); setGeoForm({ country: '', region: null, city: null }); }}
                        className="text-slate-400 hover:text-slate-600 text-xs font-bold uppercase tracking-widest px-4 py-2"
                        data-testid="geo-cancel-btn"
                      >
                        {t('monitor.отмена')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            
            {/* Onboarding CTA when no locations */}
            {selectedGeoView === 'all' && geoLocations.length === 0 && !showGeoAdd && (
              <div className="mb-12 bg-[#F5F3EE] rounded-lg p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-[#1BB594]/10 flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-7 h-7 text-[#1BB594]" />
                </div>
                <h3 className="text-lg font-bold text-[#2F2E2F] mb-2">{t('monitor.добавьте_локацию')}</h3>
                <p className="text-sm text-slate-500 mb-2 max-w-md mx-auto">
                  {t('monitor.укажите_локацию_описание')}
                </p>
                <p className="text-xs text-slate-400 mb-5 max-w-md mx-auto">
                  {t('monitor.несколько_регионов')}
                </p>
                <button
                  onClick={() => setShowGeoAdd(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#1BB594] text-white font-bold text-sm hover:bg-[#159A7F] transition-colors rounded"
                >
                  <PlusCircle className="w-4 h-4" />
                  {t('monitor.добавить_локацию')}
                </button>
              </div>
            )}

            {/* Summary table for "All locations" view */}
            {selectedGeoView === 'all' && geoLocations.length > 0 && (
              <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="locations-summary">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F]">{t('monitor.сводка_по_локациям')}</h2>
                  {geoLocations.filter(g => g.status !== 'analyzing').length > 1 && geoLocations.some(g => g.status !== 'analyzing') && (
                    <button
                      onClick={() => geoLocations.filter(g => g.status === 'pending' || g.status === 'completed').forEach(g => handleStartGeoAnalysis(g.id))}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#1BB594] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#159A7F] transition-colors rounded"
                    >
                      <Play className="w-3.5 h-3.5" />
                      {t('monitor.запустить_все')}
                    </button>
                  )}
                </div>
                {/* Compute dynamic engine list from actual results */}
                {(() => {
                  const dataEngines = [...new Set(
                    geoLocations.flatMap(geo => {
                      const rd = geoResultsCache[geo.id];
                      return rd?.ai_engine_results ? Object.keys(rd.ai_engine_results) : [];
                    })
                  )];
                  const engList = dataEngines.length > 0 ? dataEngines.filter(e => e !== 'DeepSeek') : Object.keys(AI_ENGINE_COLORS);
                  return (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E2DDD8]">
                        <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-widest text-slate-500">{t('monitor.локация')}</th>
                        <th className="text-center py-3 px-2 text-xs font-bold uppercase tracking-widest text-slate-500">{t('monitor.статус')}</th>
                        {engList.map(eng => (
                          <th key={eng} className="text-center py-3 px-2 text-xs font-bold uppercase tracking-widest text-slate-500">
                            <div className="flex items-center justify-center gap-1">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: AI_ENGINE_COLORS[eng] || '#94A3B8' }} />
                              <span className="hidden lg:inline">{eng}</span>
                            </div>
                          </th>
                        ))}
                        <th className="text-center py-3 px-2 text-xs font-bold uppercase tracking-widest text-slate-500">{t('monitor.общий_sov')}</th>
                        <th className="text-center py-3 px-2 text-xs font-bold uppercase tracking-widest text-slate-500"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {geoLocations.map(geo => {
                        const r = geoResultsCache[geo.id];
                        const engines = r?.ai_engine_results || {};
                        const sovColor = (val) => val > 50 ? 'text-emerald-600 bg-emerald-50' : val > 20 ? 'text-amber-600 bg-amber-50' : val > 0 ? 'text-red-600 bg-red-50' : 'text-slate-400';
                        return (
                          <tr
                            key={geo.id}
                            className="border-b border-[#E2DDD8] hover:bg-[#F5F3EE] cursor-pointer transition-colors"
                            onClick={() => navigate(`/monitor/${id}/geo/${geo.id}`)}
                            data-testid={`summary-row-${geo.id}`}
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-3.5 h-3.5 text-[#1BB594]" />
                                <span className="font-bold text-slate-800">{geo.geo_label}</span>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-center">
                              {geo.status === 'completed' && <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2 py-1">{t('monitor.готово')}</span>}
                              {geo.status === 'analyzing' && <RefreshCw className="w-3 h-3 text-amber-500 animate-spin mx-auto" />}
                              {geo.status === 'pending' && <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 bg-slate-50 px-2 py-1">{t('monitor.новая_статус')}</span>}
                              {geo.status === 'error' && <span className="text-[10px] font-bold uppercase tracking-widest text-red-500 bg-red-50 px-2 py-1">{t('monitor.ошибка')}</span>}
                            </td>
                            {engList.map(eng => (
                              <td key={eng} className="py-3 px-2 text-center">
                                {r ? (
                                  <span className={`text-xs font-bold px-2 py-1 ${sovColor(engines[eng]?.visibility_percent || 0)}`}>
                                    {(engines[eng]?.visibility_percent || 0).toFixed(0)}%
                                  </span>
                                ) : <span className="text-xs text-slate-300">—</span>}
                              </td>
                            ))}
                            <td className="py-3 px-2 text-center">
                              {r ? (
                                <span className={`text-sm font-black ${sovColor(r.sov_overall)}`}>
                                  {r.sov_overall?.toFixed(1)}%
                                </span>
                              ) : <span className="text-xs text-slate-300">—</span>}
                            </td>
                            <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                              {geo.status === 'pending' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleStartGeoAnalysis(geo.id); }}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#1BB594] text-white text-xs font-bold hover:bg-[#159A7F] transition-colors rounded"
                                >
                                  <Play className="w-3 h-3" />
                                  {t('monitor.запустить_мониторинг')}
                                </button>
                              )}
                              {geo.status === 'analyzing' && (
                                <button
                                  disabled
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-300 text-amber-600 text-xs font-bold rounded animate-pulse cursor-not-allowed"
                                >
                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                  {t('monitor.мониторинг_запущен')}
                                </button>
                              )}
                              {geo.status === 'completed' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleStartGeoAnalysis(geo.id); }}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#1BB594] text-white text-xs font-bold hover:bg-[#159A7F] transition-colors rounded"
                                >
                                  <Play className="w-3 h-3" />
                                  {t('monitor.запустить_мониторинг')}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                  );
                })()}
              </div>
            )}

        {/* D4: Editable Company Profile */}
        <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="company-profile">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F]">{t('monitor.профиль_компании')}</h2>
            {monitor.company_info && !editingCompanyInfo && (
              <button
                onClick={() => openCompanyForm(monitor.company_info)}
                className="text-xs font-bold uppercase tracking-widest text-[#1BB594] hover:underline"
              >
                {t('monitor.редактировать')}
              </button>
            )}
          </div>

          

          {editingCompanyInfo && (
            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('monitor.название_компании')}</label>
                <input type="text" value={companyForm.company_name}
                  onChange={e => setCompanyForm(f => ({...f, company_name: e.target.value}))}
                  placeholder={t('monitor.ph_название_компании')} className="w-full border border-[#E2DDD8] px-3 py-2 text-sm focus:border-[#1BB594] focus:outline-none" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('monitor.виды_деятельности')}</label>
                  <input type="text" value={companyForm.activities}
                    onChange={e => setCompanyForm(f => ({...f, activities: e.target.value}))}
                    placeholder={t('monitor.ph_деятельности')}
                    className="w-full border border-[#E2DDD8] px-3 py-2 text-sm focus:border-[#1BB594] focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('monitor.товары_и_услуги')}</label>
                  <input type="text" value={companyForm.products_services}
                    onChange={e => setCompanyForm(f => ({...f, products_services: e.target.value}))}
                    placeholder={t('monitor.ph_товары')}
                    className="w-full border border-[#E2DDD8] px-3 py-2 text-sm focus:border-[#1BB594] focus:outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('monitor.целевая_аудитория')}</label>
                  <input type="text" value={companyForm.target_audience}
                    onChange={e => setCompanyForm(f => ({...f, target_audience: e.target.value}))}
                    placeholder={t('monitor.ph_аудитория')}
                    className="w-full border border-[#E2DDD8] px-3 py-2 text-sm focus:border-[#1BB594] focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('monitor.утп')}</label>
                  <input type="text" value={companyForm.usp}
                    onChange={e => setCompanyForm(f => ({...f, usp: e.target.value}))}
                    placeholder={t('monitor.ph_утп')}
                    className="w-full border border-[#E2DDD8] px-3 py-2 text-sm focus:border-[#1BB594] focus:outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('monitor.география')}</label>
                  <input type="text" value={companyForm.geography}
                    onChange={e => setCompanyForm(f => ({...f, geography: e.target.value}))}
                    placeholder={t('monitor.ph_география')}
                    className="w-full border border-[#E2DDD8] px-3 py-2 text-sm focus:border-[#1BB594] focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('monitor.алиасы_бренда')}</label>
                  <input type="text" value={companyForm.brand_aliases}
                    onChange={e => setCompanyForm(f => ({...f, brand_aliases: e.target.value}))}
                    placeholder={t('monitor.ph_алиасы')}
                    className="w-full border border-[#E2DDD8] px-3 py-2 text-sm focus:border-[#1BB594] focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('monitor.исключения_контекста')}</label>
                  <input type="text" value={companyForm.exclusion_contexts}
                    onChange={e => setCompanyForm(f => ({...f, exclusion_contexts: e.target.value}))}
                    placeholder={t('monitor.ph_исключения')}
                    className="w-full border border-[#E2DDD8] px-3 py-2 text-sm focus:border-[#1BB594] focus:outline-none" />
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button onClick={handleSaveCompanyInfo}
                  disabled={savingCompanyInfo || !companyForm.company_name.trim()}
                  className="text-xs font-bold uppercase tracking-widest px-8 py-3 bg-[#1BB594] text-white hover:bg-[#159A7F] disabled:opacity-50">
                  {savingCompanyInfo ? t('monitor.сохранение') : t('monitor.сохранить_профиль')}
                </button>
                <button onClick={() => setEditingCompanyInfo(false)}
                  className="text-xs font-bold uppercase tracking-widest px-4 py-3 text-slate-500 hover:text-slate-700">
                  {t('monitor.отмена')}
                </button>
              </div>
            </div>
          )}

          {monitor.company_info && !editingCompanyInfo && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('monitor.компания')}</p>
                  <p className="text-sm font-bold text-slate-800">{monitor.company_info.company_name}</p>
                </div>
                {monitor.company_info.target_audience && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('monitor.целевая_аудитория')}</p>
                    <p className="text-xs text-slate-700">{monitor.company_info.target_audience}</p>
                  </div>
                )}
                {monitor.company_info.usp && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('monitor.утп')}</p>
                    <p className="text-xs text-slate-700">{monitor.company_info.usp}</p>
                  </div>
                )}
                {monitor.company_info.geography && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('monitor.география')}</p>
                    <p className="text-xs text-slate-700">{monitor.company_info.geography}</p>
                  </div>
                )}
              </div>
              {(monitor.company_info.activities?.length > 0 || monitor.company_info.products_services?.length > 0) && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {(monitor.company_info.activities || []).map((a, i) => (
                    <span key={`a-${i}`} className="text-xs px-2 py-1 bg-slate-100 text-slate-600 border border-slate-200">{a}</span>
                  ))}
                  {(monitor.company_info.products_services || []).map((p, i) => (
                    <span key={`p-${i}`} className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200">{p}</span>
                  ))}
                </div>
              )}
              {(monitor.company_info.brand_aliases?.length > 0 || monitor.company_info.exclusion_contexts?.length > 0) && (
                <div className="mt-4 pt-4 border-t border-[#E2DDD8]">
                  {monitor.company_info.brand_aliases?.length > 0 && (
                    <div className="mb-2">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-2">{t('monitor.алиасы')}</span>
                      {monitor.company_info.brand_aliases.map((a, i) => (
                        <span key={`ba-${i}`} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 mr-1">{a}</span>
                      ))}
                    </div>
                  )}
                  {monitor.company_info.exclusion_contexts?.length > 0 && (
                    <div>
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-2">{t('monitor.исключения')}</span>
                      {monitor.company_info.exclusion_contexts.map((e, i) => (
                        <span key={`ec-${i}`} className="text-xs px-2 py-0.5 bg-red-50 text-red-600 border border-red-200 mr-1">{e}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

          </div>
        )}


          {activeTab === 'monitoring' && (
            <></>
          )}
        {activeTab === 'webmaster' && (
          <div data-testid="webmaster-tab-content">
            <WebmasterChat
              monitorId={id}
              companyName={monitor.company_info?.company_name || monitor.company_url}
              companyUrl={monitor.company_url}
            />
          </div>
        )}

        {activeTab === 'promotion' && (
          <div data-testid="promotion-tab-content">
            <PromoTab monitorId={id} monitor={monitor} geoLocations={geoLocations} />
          </div>
        )}
      </main>
    </div>
  );
};

export default MonitorDetails;
